import React from 'react'
import { EMAILS } from 'shared-constants'
import styled, { css } from 'styled-components'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Box } from 'components/Layout'
import { PureSSRIndicator } from 'components/PureSSRIndicator'
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE_FULL,
} from 'constants/common'
import { FONT_WEIGHT, mq, SPACE } from 'Theme'
import { prepareGetStaticNavigationProps } from '../utils/prepareGetStaticNavigationProps'

const Strong = styled('strong')`
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};
`

const LiWithStyledMarker = styled('li')`
  &::marker {
    font-weight: ${FONT_WEIGHT.MEDIUM};
  }
`

const scrollMarginTop = css`
  scroll-margin-top: ${HEADER_HEIGHT_MOBILE_FULL};
  ${mq.from.TABLET`
      scroll-margin-top: ${HEADER_HEIGHT_DESKTOP}; 
   `}
`

const H2 = styled('h2')`
  ${scrollMarginTop}
`

const H3 = styled('h3')`
  ${scrollMarginTop}
`

const H4 = styled('h4')`
  ${scrollMarginTop}
`

const LinkWithUnderline = styled('a')`
  text-decoration: underline;
  text-underline-offset: 0.15em;
`

const PrivacyPolicy = () => (
  <Box mt={SPACE.PX_80} mb={SPACE.PX_120}>
    <H2>The Expert - Privacy Notice</H2>
    <H3>Last Updated: April 9, 2025</H3>
    <p>
      This Privacy Notice describes the data collection, use, protection, and
      privacy practices of The Expert, Inc. (&ldquo;<Strong>The Expert</Strong>
      ,&rdquo; &ldquo;<Strong>we</Strong>,&rdquo; &ldquo;<Strong>us</Strong>
      ,&rdquo; and/or &ldquo;<Strong>our</Strong>&rdquo;) with respect to
      information and data we may collect, receive and/or process in connection
      with your use of The Expert Service (as defined in Section 3 below).
    </p>
    <p>
      We ask that you please read this Privacy Notice carefully to understand
      our policies and practices regarding your information and how we will
      treat it. If you have any questions, comments, or concerns regarding this
      Privacy Notice and/or our data practices, please{' '}
      <LinkWithUnderline href="#how-to-contact-us">
        contact us
      </LinkWithUnderline>{' '}
      as set forth in Section 18 below.
    </p>
    <p>
      BY VISITING OR USING THE EXPERT SERVICE (OR ANY PART THEREOF), INCLUDING,
      BUT NOT LIMITED TO, SUBMITTING ORDERS TO PURCHASE PRODUCTS, SERVICES,
      AND/OR CONSULTATIONS, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS PRIVACY
      NOTICE AND AGREE TO THE EXPERT&rsquo;S COLLECTION AND USE OF YOUR PERSONAL
      INFORMATION AS DESCRIBED HEREIN.
    </p>
    <br />
    <H3>Table of Contents</H3>
    <ol id="table-of-contents">
      <li>
        <LinkWithUnderline href="#changes-to-this-privacy-notice">
          Changes to this Privacy Notice
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#who-processes-your-personal-data">
          Who Processes Your Personal Data
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#information-we-collect-and-process-legal-basis-for-processing">
          Information We Collect and Process; Legal Basis for Processing
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#childrens-privacy">
          Children&rsquo;s Privacy
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#sensitive-data">
          Sensitive Data
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#how-the-expert-uses-personal-information">
          How The Expert Uses Personal Information
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#sharing-personal-information">
          Sharing Personal Information
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#marketing-comunications">
          Marketing Communications
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#accessing-updating-and-correcting-personal-information">
          Accessing, Updating and Correcting Personal information
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#third-party-social-plug-ins">
          Third Party Social Plug-Ins
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#external-websites">
          External Websites
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#data-retention">
          Data Retention
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#data-security-and-protection">
          Data Security and Protection
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#european-privacy-rights">
          European Privacy Rights{' '}
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#california-privacy">
          California Privacy Rights &ndash; Shine the Light
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#nevada-privacy">
          Nevada Privacy Rights
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#international-transfers">
          International Transfers
        </LinkWithUnderline>
      </li>
      <li>
        <LinkWithUnderline href="#how-to-contact-us">
          How to Contact Us
        </LinkWithUnderline>
      </li>
    </ol>
    <br />
    <H3 id="changes-to-this-privacy-notice">
      1. Changes to this Privacy Notice
    </H3>
    <p>
      The Expert reserves the right to update or modify this Privacy Notice at
      any time.&nbsp;Except for material changes as described below in this
      Section, all updates and modifications to these documents will be
      effective from the day they are posted on the Site at{' '}
      <LinkWithUnderline href="https://www.theexpert.com/privacy">
        https://www.theexpert.com/privacy
      </LinkWithUnderline>
      .&nbsp;If we make any material changes to this Privacy Notice, we will
      provide reasonable prior notice of these changes by posting a prominent
      notice of any such changes on the Site, and if we have an email address on
      file for you, sending a notification to such email address.&nbsp;Material
      changes to this Privacy Notice will become effective on the date set forth
      in the notice.&nbsp;It is your responsibility to regularly visit and
      review this Privacy Notice.
    </p>
    <p>
      If you do not agree to any updates or modifications to the Privacy Notice,
      do not visit or use The Expert Service or purchase any Products, Services
      and/or Consultations.&nbsp;Your continued use of The Expert Service,
      including, but not limited to, the purchase of any Products, Services
      and/or Consultations after the applicable effective date of the revised
      Privacy Notice, signifies to us that you acknowledge and agree to be bound
      by the revised Privacy Notice.
    </p>
    <br />
    <H3 id="who-processes-your-personal-data">
      2. Who Processes Your Personal Data?
    </H3>
    <p>
      Your personal data are being processed by The Expert (as the &ldquo;
      <Strong>Controller</Strong>&ldquo;).
    </p>
    <p>
      To learn more about personal data management or if you have any other
      questions, please follow the details in How to Contact Us.
    </p>
    <br />
    <H3 id="information-we-collect-and-process-legal-basis-for-processing">
      3. Information We Collect and Process; Legal Basis for Processing
    </H3>
    <H4>
      <u>Information we Collect and Process</u>
    </H4>
    <p>
      The term &ldquo;personal information&rdquo; or &ldquo;personal data&rdquo;
      means any information about an individual from which that person may be
      identified.&nbsp;For example, it may include a person&rsquo;s name,
      telephone number, email address, or payment information, and in some
      jurisdictions, IP address.&nbsp;It does not include data from which the
      identity of an individual has been definitively removed along with any
      identifiers connected to such individual (also known as anonymous or
      anonymized data).
    </p>
    <p>
      How and what personal information we collect in connection with the use of
      The Expert Service depends on how and why you use them.&nbsp;For instance,
      the information that you provide when you visit and/or browse the Site or
      sign up for our marketing communications is more limited than the
      information you provide when you purchase Products, Consultations and/or
      Services.
    </p>
    <p>
      Generally, The Expert collects, processes and/or receives personal
      information for the following purposes:
    </p>
    <ul>
      <li>
        <Strong>To provide our services</Strong> including:
        <ul>
          <li>
            When visiting, browsing and/or using our website located at{' '}
            <LinkWithUnderline href="http://www.theexpert.com">
              www.theexpert.com
            </LinkWithUnderline>{' '}
            (the &ldquo;
            <Strong>Site</Strong>&rdquo;), including, but not limited to, using
            the Site to search for third party interior designers (&ldquo;
            <Strong>Designers</Strong>&rdquo;), and/or visiting or browsing our
            online marketplace platform (the &ldquo;<Strong>Marketplace</Strong>
            &rdquo;);
          </li>
          <li>
            When booking an interior design consultation (each, a &ldquo;
            <Strong>Consultation</Strong>&rdquo;) with a Designer;
          </li>
          <li>
            When registering and/or providing Consultations as a Designer;
          </li>
          <li>
            When purchasing home goods and/or products (&ldquo;
            <Strong>Products</Strong>&rdquo;) that we make available through the
            Marketplace and/or that we purchase on your behalf through our trade
            and/or reseller accounts with third party vendors (hereinafter, the
            &ldquo;<Strong>Trade Vendors</Strong>&rdquo;);
          </li>
          <li>
            In connection with the provisions of services we provide or make
            available in connection with The Expert Service, including, but not
            limited to, any delivery and/or installations services that The
            Expert provides through its white-glove delivery providers (the
            &ldquo;
            <Strong>Delivery Services</Strong>&rdquo;), connecting users with
            Designers, purchasing Products on behalf of users through Trade
            Vendors, and/or any related customer support services (all of the
            foregoing, collectively, the &ldquo;<Strong>Services</Strong>
            &rdquo;);
          </li>
        </ul>
      </li>
      <li>
        <Strong>
          To facilitate any sweepstakes, contest, or promotional activities
        </Strong>
        (each, a &quot;<Strong>Promotion</Strong>&quot;) that The Expert
        sponsors or is involved with;
      </li>
      <li>
        <Strong>To provide marketing</Strong>, such as:
        <ul>
          <li>
            When you interact with us or our account page, and/or our content or
            posts through social media platforms, such as, Instagram, Facebook,
            Twitter and/or YouTube (each, a &ldquo;
            <Strong>Social Media Platform</Strong>&rdquo;);&nbsp;
          </li>
          <li>
            When signing up for our newsletters and/or marketing communications,
            including, without limitation, signing up for our text messaging
            service (the &quot;<Strong>Text Service</Strong>&quot;);
          </li>
          <li>
            When you are referred to our Services by a participant in any of our
            referral programs.
          </li>
        </ul>
      </li>
      <li>
        <Strong>To communicate with you if you contact us</Strong> directly,
        including, but not limited to, when you contact us through Social Media
        Platforms.
      </li>
    </ul>
    <p>
      When we use the term &ldquo;<Strong>The Expert Service</Strong>&rdquo;
      throughout this Privacy Notice, we mean, individually and collectively,
      the Site, the Marketplace, and the Services.
    </p>
    <h4>
      <u>Legal basis for Processing</u>
    </h4>
    <p>
      The Expert processes your personal data on the legal basis as follows:
    </p>
    <ul>
      <li>
        In order to perform and/or provide The Expert Service you interact with
        and/or request. This may also include disclosure to the third parties
        who help us perform our obligations to you in connection with your use
        of The Expert Service, such as hosting, customer support, and/or
        delivery providers.
      </li>
      <li>
        Where it is necessary for our legitimate interests (or those of a third
        party), and your interests and fundamental rights do not override those
        interests. For example, for security purposes and protection against
        fraud.
      </li>
      <li>
        Where we need to comply with a legal or regulatory obligation. For
        example, keeping records of our sales for tax compliance.
      </li>
      <li>
        If we have obtained your prior consent (for example, where required by
        law, when you subscribe to our newsletter or participate in our surveys
        or marketing campaigns). Please note that for this specific legal basis,
        you have the right to withdraw your consent at any time.
      </li>
    </ul>
    <ol type="a">
      <LiWithStyledMarker>
        <H3>Information Provided Directly to The Expert</H3>
        <p>
          The Expert collects the following types of information directly from
          you.&nbsp;Please be advised that we may ask you to update your
          information from time to time in order to keep it accurate.
        </p>
        <br />
        <ol type="i">
          <LiWithStyledMarker>
            <H4>
              <em>Account &amp; Contact Information</em>
            </H4>
            <p>
              If you sign-up and register an account for The Expert Service
              and/or purchase Products, Consultations and/or Delivery Services,
              we will collect contact information such as:
            </p>
            <ul>
              <LiWithStyledMarker>
                Your identity (first and last name).
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Your username, password and/or other login credentials.
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Your shipping address and related information.
              </LiWithStyledMarker>
              <LiWithStyledMarker>Your phone number.</LiWithStyledMarker>
            </ul>
            <p>
              If you are a Designer, in addition to the information above, The
              Expert may collect the following information when you set-up
              and/or update your Designer page and/or profile:
            </p>
            <ul>
              <LiWithStyledMarker>
                Information about your business, such as the legal business
                name, business phone number, and business address.
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Your Social Media Platform usernames and/or handles.
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Your photo, biographical information, portfolio work,
                qualifications, licenses, specialties, and any other information
                you decide to share to your public profile.
              </LiWithStyledMarker>
            </ul>
            <br />
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            <H4>
              <em>Payment and Transaction Information</em>
            </H4>
            <p>
              In certain circumstances, we may collect certain payment
              information, such as your credit card, bank account information,
              when you purchase any Products, Consultations and/or Delivery
              Services, or if you are Designer, your taxpayer identification
              information (e.g., your employer identification number or social
              security number).
            </p>
            <p>
              Note that we do not directly collect or store any payment and/or
              billing information.&nbsp;Payment transaction information is
              processed through our third-party PCI-compliant payment processors
              (currently,{' '}
              <LinkWithUnderline href="https://stripe.com/">
                Stripe
              </LinkWithUnderline>
              ), on our behalf in order to collect fees payable by you in
              connection with any Services, Consultations and/or Products
              purchased by you.
            </p>
            <p>
              The information provided to The Expert only includes partial
              payment or credit card information, and limited transaction
              information via our payment processors&rsquo; portals.&nbsp;Please
              review the following Privacy Policies of our current payment
              providers to learn more about how they collect, process and
              protect your personal information:
            </p>
            <ul>
              <LiWithStyledMarker>
                Stripe:&nbsp;
                <LinkWithUnderline href="https://stripe.com/privacy">
                  https://stripe.com/privacy
                </LinkWithUnderline>
              </LiWithStyledMarker>
            </ul>
            <br />
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            <H4>
              <em>Other Information You May Provide to Us</em>
            </H4>
            <p>
              In addition to the above scenarios, there may be certain features
              of The Expert Service which allow you to provide additional
              information to us directly, which may include:
            </p>
            <ul>
              <LiWithStyledMarker>
                Your name (first and last) and/or email address, when you sign
                up for our email marketing communications;
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Your mobile phone number if you opt-in to our Text Service;
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Information that you provide when you enter into and/or
                participate in a Promotion through the Site and/or in connection
                with your use of The Expert Service.&nbsp;Each Promotion that we
                sponsor and manage has its own terms and conditions or official
                rules, which you should review as they may contain other
                requirements (such as, allowing use of your name or likeness in
                advertising unless prohibited by applicable law);
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Messages, photos, video, audio, images, data, information, text
                and/or any other content or materials submitted by you when you
                use any interactive communication features of The Expert Service
                that may permit you to comment on any Product listings in the
                Marketplace and/or any blogs and/or forums we make available
                through the Site; and/or
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Information, data, content, documents and/or materials you
                provide to us when you fill out forms, answer surveys, or
                contact us (such as your feedback, requests for support, or
                other communications with us, including, but not limited,
                communications via phone calls and/or text messages).
              </LiWithStyledMarker>
              <LiWithStyledMarker>
                Contact information, including name and email, of someone that
                you refer to us if you participate in any of our referral
                programs.
              </LiWithStyledMarker>
            </ul>
            <br />
          </LiWithStyledMarker>
        </ol>
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H3>
          Information Collected from Third Parties (someone other than you)
          and/or Third Party Sources
        </H3>
        <p>
          In some instances, we may process and/or receive personal information
          from third parties or third party sources as, such as:
        </p>
        <ul>
          <LiWithStyledMarker>
            Information from our third party service providers (as described in
            Section 7(a) below).
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Information from other users that you engage with in connection with
            The Expert Service, for example, if you are a Designer, we may
            receive information about you from the client and/or customer who
            purchased your Consultations, and if you are client and/or customer,
            we may receive information about from the Designer that you booked
            your Consultation with. In addition, we may refer information about
            you from current users that refer you to our Services as part of our
            referral programs.
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Information from Trade Vendors related to Products you purchase from
            such Trade Vendors.
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Information and/or data from certain third-party products, services
            and/or applications (including, without limitation, including,
            without limitation, data products and services) that The Expert
            Service may integrate and/or enable you to utilize certain in
            connection with the use of The Expert Service (collectively, &ldquo;
            <Strong>Third-Party Integrations</Strong>&rdquo;).&nbsp;For example,
            if you sign-in or register an account using single sign-on services
            through your account with a third party (such as, Google), we may
            receive information from the third party provider that manages your
            account.
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Information and/or content that you provide and/or that may be
            provided to us, when you engage with and/or interact with us through
            a Social Media Platform.&nbsp;For example, if you like, comment,
            and/or view Courses we have made available through our YouTube
            account, or if you tag us in an of your social media posts, we will
            have access to that information and content.&nbsp;We only use this
            information to respond to and/or engage with you on Social Media
            Platforms and not for any other purposes.
          </LiWithStyledMarker>
        </ul>
        <p>
          <em>
            Please note that we do not control or operate the Social Media
            Platforms or the use of your information by the providers of the
            Social Media Platforms and/or other users of the Social Media
            Platforms.&nbsp;You should review the privacy notices for any Social
            Media Platforms you use to understand how they collect, use, share
            and protect your information.
          </em>
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H3>Usage Information; Cookies &amp; Targeted Advertising</H3>
        <ol type="i">
          <LiWithStyledMarker>
            <H4>
              <em>Usage Information</em>
            </H4>
            <p>
              Just like many websites and services, we, or our authorized
              third-party service providers, automatically collect technical
              and/or analytics information about how you use and/or interact
              with our Site and/or Marketplace (collectively, &ldquo;
              <Strong>Usage Information</Strong>
              &rdquo;).
            </p>
            <p>
              Usage Information may include (but is not necessarily limited to)
              identifiers, commercial information, and internet activity
              information such as IP address (or proxy server), mobile device
              number, device and application information, identification numbers
              and features, your approximate location (as determined through
              your IP address), browser type, plug-ins, integrations, Internet
              service provider or mobile carrier, the pages and files viewed,
              website and webpage interactions including searches and other
              actions you take, websites, apps and/or ads that referred you to
              the website, advertising and language preferences, operating
              system type and version, system configuration information, date
              and time stamps associated with your usage, and frequency of
              visits to the websites, and details of which of our products and
              product versions you are using.
            </p>
            <p>
              Some of the device and usage data collected, whether alone or in
              conjunction with other data, could identify you. Please note that
              this device and usage data is primarily used to identify the
              uniqueness of each user logging on (as opposed to specific
              individuals), apart from where it is strictly required to identify
              an individual for security purposes.
            </p>
            <p>
              Typically, all Usage Information is collected in anonymous form
              and does not identify you personally.&nbsp;For example, some of
              the device and usage information collected, whether alone or in
              conjunction with other data, could identify you, although, except
              where strictly required to identify an individual for security
              purposes, such data and information is primarily used to identify
              the uniqueness of each user logging on (as opposed to specific
              individuals).&nbsp;To the extent any Usage Information is linked
              or tied to personally identifiable information, such information
              is deemed &ldquo;personal data&rdquo; and we will use it and
              protect it in accordance with this Privacy Notice.
            </p>
            <br />
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            <H4>
              <em>Cookies and Similar Technologies</em>
            </H4>
            <p>
              We or authorized third parties may collect certain information
              (including, without limitation, Usage Information) by automated
              means using technologies such as cookies, web beacons, embedded
              scripts, pixels, browser analysis tools, server logs, and mobile
              identifiers, and similar technologies that are deployed, and
              typically stored on your device and have a unique identifier, when
              you visit a website, when you open an email or click on links or
              images within an email, and/or when you interact with online
              advertisements. If consent for the use of such cookies and/or
              automated technologies is required by applicable laws or
              regulations in your location, then such cookies and/or automated
              technologies are only used by The Expert in this way if we have
              obtained such consent. For more information on our use of these
              technologies, see our{' '}
              <LinkWithUnderline href="/blog/cookie-policy">
                Cookie Policy
              </LinkWithUnderline>
              .
            </p>
            <br />
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            <H4 id="targeting-and-advertising">
              <em>Targeting &amp; Advertising</em>
            </H4>
            <p>
              We may use third party advertising partners (e.g., Facebook, Inc.
              and Google, Inc.) who use targeting/advertising cookies and
              similar technologies to deliver advertisements that are more
              relevant to you and your interests. If consent for the use of
              targeting and advertising cookies is required by applicable laws
              or regulations in your location, then such cookies are only used
              by The Expert in this way if we have obtained such consent. They
              are also used to limit the number of times you see an
              advertisement as well as help measure the effectiveness of the
              advertising campaigns. They are usually placed by advertising
              networks with the website operator&rsquo;s permission. They
              remember that you have visited a website and this information is
              shared with other organizations such as advertisers.
              &nbsp;Targeting or advertising cookies will often be linked to
              site functionality provided by the other organizations.For more
              information on our and/or third-party advertising partners` use of
              these targeting &amp; advertising cookies and similar
              technologies, please see our{' '}
              <LinkWithUnderline href="/blog/cookie-policy">
                Cookie Policy
              </LinkWithUnderline>
              .
            </p>
            <br />
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            <H4 id="how-to-manage-cookie-preferences">
              <em>How to Manage Cookie Preferences</em>
            </H4>
          </LiWithStyledMarker>
          <p>
            Where required by applicable law, we will obtain your consent prior
            to setting cookies (except for strictly necessary cookies). Please
            see our{' '}
            <LinkWithUnderline href="/blog/cookie-policy">
              Cookie Policy
            </LinkWithUnderline>{' '}
            to see how you can manage your cookie and tracking preferences.
          </p>
          <br />
        </ol>
        <LiWithStyledMarker>
          <H4>Aggregated Data</H4>
          <p>
            With the personal information and other data (including, Usage
            Information) collected by us, we process &ldquo;
            <em>Aggregated Data</em>
            &rdquo;, such as statistical or demographic data.&nbsp;Aggregated
            Data may be derived from personal information, but is not considered
            personal information under the law if it does not directly or
            indirectly reveal your identity.&nbsp;If we combine or connect
            Aggregated Data with your personal information so that it can
            directly or indirectly identify you, we treat the combined data as
            personal information, which will be processed in accordance with
            this Privacy Notice.
          </p>
        </LiWithStyledMarker>
      </LiWithStyledMarker>
    </ol>
    <H3 id="childrens-privacy">4. Children&rsquo;s Privacy</H3>
    <p>
      The Expert does not target The Expert Service to persons under the age of
      18 and does not knowingly collect personal information from persons under
      the age of 18.&nbsp;Therefore, we ask you not to provide us with personal
      information of persons under the age of 18. If we learn that personal
      information of persons under 18 years of age has been collected on or
      through The Expert Service or otherwise provided to us, then we may
      deactivate the account or otherwise terminate access to The Expert Service
      and/or make the content and/or information inaccessible.
    </p>
    <br />
    <H3 id="sensitive-data">5. Sensitive Data</H3>
    <p>
      The Expert does not require you to provide any sensitive data about your
      race or ethnicity, religious or philosophical beliefs, sex life, sexual
      orientation, political opinions, trade union membership, health and
      genetics, or biometric data to use The Expert Service.
    </p>
    <br />
    <H3 id="how-the-expert-uses-personal-information">
      6. How The Expert Uses Personal Information
    </H3>
    <p>
      Depending on how you use The Expert Service, we use your personal
      information in the following instances:
    </p>
    <ul>
      <li>
        To host and provide the Site and Marketplace, and perform our Services
        to you and other visitors/customers;
      </li>
      <li>
        To deliver direct marketing communications regarding our products and
        services that we may think are of interest to you;
      </li>
      <li>
        To ship and deliver Products you have purchased from us through the
        Site;
      </li>
      <li>
        If you are a Designer, to market you and your participation and/or
        offerings through The Expert Service;
      </li>
      <li>
        To conduct and administer a Promotion if you have chosen to participate;
      </li>
      <li>To administer our referral programs;</li>
      <li>
        To respond to your queries and requests, or otherwise communicate
        directly with you;
      </li>
      <li>
        To detect any fraudulent or illegal activity against you, our other
        users, and/or The Expert;
      </li>
      <li>
        To perform system maintenance and upgrades, and enable new features;
      </li>
      <li>To enforce and/or protect our rights and the rights of others;</li>
      <li>
        To comply with a legal or regulatory obligation (for example, keeping
        records of our sales for tax compliance); and
      </li>
      <li>
        To provide information to regulatory bodies when legally required, and
        only as outlined below in the Section titled &ldquo;
        <em>Required Disclosures &ndash; Responding to Legal Orders</em>&rdquo;
        under{' '}
        <LinkWithUnderline href="#legal-obligations-and-security">
          Legal Obligations, Required Disclosures, and Exigent Circumstances.
        </LinkWithUnderline>
      </li>
    </ul>
    <p>In addition, we may use Usage Information for the following purposes:</p>
    <ul>
      <li>
        To compile statistics and analytics about your use of and interaction
        with The Expert Services, including details about how and where The
        Expert Service is accessed, how often you visit or use The Expert
        Service, the date and time of your visits, your actions on the Site
        and/or Marketplace, and other similar traffic, usage, and trend data,
        which allow us to better understand the functionality of our Site and
        Marketplace on your device and gain insights about how The Expert
        Service is used in order to improve your experience;
      </li>
      <li>
        To gather important functionality, testing, and performance data about
        the Site and Marketplace, including performance data related to the
        Site, such as networking activity, CPU load, and resource usage, among
        other relevant data;
      </li>
      <li>
        To moderate user behavior on the Site, such as protecting against fraud
        and other inappropriate activities;
      </li>
      <li>To gather demographic information about our user base;</li>
      <li>
        To provide you with notifications, recommendations, and information
        about specific Products and/or features of The Expert Service and/or
        additional products, services, or features we believe may be of interest
        to you; and
      </li>
      <li>
        Advertise The Expert, The Expert Service, Designers, and the Products
        across the Internet (as further explained in{' '}
        <LinkWithUnderline href="#targeting-and-advertising">
          Targeting &amp; Advertising
        </LinkWithUnderline>{' '}
        above).
      </li>
    </ul>
    <br />
    <H3 id="sharing-personal-information">7. Sharing Personal Information</H3>
    <p>
      Aside from disclosing your information to those of our personnel who are
      authorized to process the information in order to provide The Expert
      Service and who are committed to confidentiality, we disclose your
      personal information only to the third parties as described below.
    </p>
    <ol type="a">
      <LiWithStyledMarker>
        <H4>Third-Party Service Providers/Sub-Processors</H4>
        <p>
          We share personal information with third parties that provide services
          to us that help us in the operation, provision, administration and
          management of The Expert Service (&ldquo;
          <Strong>Service Providers</Strong>&rdquo;), and to otherwise operate
          our business.&nbsp;Depending on how you use The Expert Service, the
          following categories of third parties collect data on our behalf or
          receive personal information:
        </p>
        <ul>
          <LiWithStyledMarker>Hosting services providers,</LiWithStyledMarker>
          <LiWithStyledMarker>Analytics providers,</LiWithStyledMarker>
          <LiWithStyledMarker>
            Payment processing providers (currently{' '}
            <LinkWithUnderline href="https://stripe.com/">
              Stripe
            </LinkWithUnderline>
            );
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Shipping providers and carriers;
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Merchants and/or vendors of the Products we make available through
            the Marketplace (but excluding Trade Vendors, which are not our
            Service Providers as described below);
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Providers of our Delivery Services;
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Advertising and marketing partners,
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Providers of business operations and communication tools,
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Other third-party service providers that help us provide features
            and functions for The Expert Service (e.g., customer support
            providers), and
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            Professional service providers, such as auditors, lawyers,
            consultants, accountants and insurers.
          </LiWithStyledMarker>
        </ul>
        <p>
          For a list of all Service Providers we use, please{' '}
          <LinkWithUnderline href="#how-to-contact-us">
            contact us
          </LinkWithUnderline>
          .
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>To Designers and/or Trade Vendors</H4>
        <p>
          If you are a client and/or customer that purchases a Consultation with
          a Designer and/or a Product from a Trade Vendor, we will share your
          information with the applicable Designer and/or Trade Vendor related
          to your purchase.&nbsp;Note that Designers and Trade Vendors are not
          our service providers.&nbsp;The applicable Designer and/or Trade
          Vendor may use your personal information differently than we do and we
          do not control their use of your information.&nbsp;Please consult
          directly with the Designers and Trade Vendors regarding their privacy
          notices and data collection practices.
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>To Other Users of The Expert Service</H4>
        <p>
          If you communicate through and/or comment or post using any public
          boards, messaging and/or communication features of The Expert Service,
          including, but not limited to, posting and reviews for Products,
          Consultations and/or Services, your communications and posts,
          including, any personal information contained therein, will be
          publicly available to other users of The Expert Service.
        </p>
        <p>
          Please be aware that other users with whom you choose to share your
          information may use your personal information differently than we do,
          and we do not control their use of your information.&nbsp;The Expert
          will not be able to contain or retrieve personal information once it
          has been shared or disclosed with other users, and The Expert will
          have no responsibility or liability for any consequences that may
          result because you have released or shared personal information with
          others.
        </p>
        <p>
          Please make your choice to share and/or publicly post information
          carefully.
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>Third Party Integrations</H4>
        <p>
          When you interact with and/or use Third Party Integrations in
          connection with The Expert Service, certain data and information may
          be exchanged between us and the provider of the applicable Third Party
          Integration.&nbsp;Information that is shared with us through Third
          Party Integration is used by us as described in this Privacy
          Notice.&nbsp;Note that the Third Party Integrations you choose to
          interact with and/or use are not our service providers.&nbsp;The
          applicable providers of Third Party Integrations may use your personal
          information differently than we do and we do not control their use of
          your information.&nbsp;Please review the privacy notices for the Third
          Party Integrations.
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>Business Transfers</H4>
        <p>
          We may also share data with third parties to whom we choose to sell,
          transfer, or merge parts of our business or our assets. Alternatively,
          we may seek to acquire other businesses or merge with them. If a
          change happens to our business, then the new owners may use your
          personal information in the same way as set out in this Privacy
          Notice.
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>Affiliates and Subsidiaries</H4>
        <p>
          Personal information that we collect may be shared with the employees,
          contractors, and agents of The Expert and our affiliated and
          subsidiary entities (&ldquo;<Strong>Affiliates</Strong>&rdquo;) who
          are involved in providing or improving The Expert Service and/or other
          products and/or services that we offer. We obligate the employees,
          contractors and agents of The Expert and our Affiliates to ensure the
          security and confidentiality of your personal information and to act
          on that personal information only in a manner consistent with this
          Privacy Notice.
        </p>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4 id="legal-obligations-and-security">
          Legal Obligations and Security
        </H4>
        <p>
          <Strong>
            <em>
              Regulatory and Government Bodies &ndash; Compliance with Law
            </em>
          </Strong>
        </p>
        <p>
          We may disclose your information to regulatory agencies and official
          government bodies, as required to comply with or satisfy any laws,
          rules, or regulations applicable to The Expert.
        </p>
        <br />
        <p>
          <Strong>
            <em>Required Disclosures &ndash; Responding to Legal Orders</em>
          </Strong>
        </p>
        <p>
          If we are required to disclose personal information by law, such as
          pursuant to a subpoena, warrant or other judicial or administrative
          order, our policy is to respond to requests that are properly issued
          by law enforcement within the United States. Under such circumstances,
          unless prohibited by applicable law, we will attempt to provide you
          with prior notice that a request for your personal information has
          been made in order to give you an opportunity to object to the
          disclosure.&nbsp;We will attempt to provide this notice by email, if
          you have given us an email address. However, government requests may
          include a court-granted non-disclosure order, which prohibits us from
          giving notice to the affected individual.&nbsp;In cases where we
          receive a non-disclosure order, we will notify you when it has expired
          or once we are authorized to do so.
        </p>
        <br />
        <p>
          <Strong>
            <em>
              Exigent Circumstances &amp; Enforcement/Protection of Our Rights
            </em>
          </Strong>
        </p>
        <p>
          Your information, including the contents of all of your online
          communications in our Services and between you and The Expert may be
          accessed and monitored as needed to provide our Services and may be
          disclosed to law enforcement, regulatory agencies, official government
          bodies, and other third parties, as we, in our sole discretion,
          believe necessary or appropriate:
        </p>
        <ul>
          <LiWithStyledMarker>
            To enforce our rights under our{' '}
            <LinkWithUnderline href="https://www.theexpert.com/terms">
              Terms of Service
            </LinkWithUnderline>{' '}
            (including, any applicable Additional Terms, guidelines, policies,
            and/or rules incorporated or referenced therein), and/or other
            agreements, terms, and/or policies we enter into with our users,
            customers, clients, and/or Designers and/or that are applicable to
            the use of The Expert Service;
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            In connection with an investigation of fraud, intellectual property
            infringement, piracy or other unlawful activity or activity that may
            expose us or our affiliates, partners and/or agents to legal
            liability; and/or
          </LiWithStyledMarker>
          <LiWithStyledMarker>
            If we receive information that provides us with a good faith belief
            that there is an exigent emergency involving the danger of death or
            serious physical injury to a person.
          </LiWithStyledMarker>
        </ul>
        <br />
      </LiWithStyledMarker>
      <LiWithStyledMarker>
        <H4>With your Consent</H4>
        <p>
          There may be situations where you are asked to consent to share
          personal information with third parties for additional reasons not
          included in this Privacy Notice.&nbsp;In such event, we will only
          share such personal information if we have received your prior consent
          and only for the purposes as listed in the request to share such
          information.
        </p>
      </LiWithStyledMarker>
    </ol>
    <br />
    <H3 id="marketing-comunications">8. Marketing Communications</H3>
    <p>
      If you are a customer of ours and have not otherwise opted out (or with
      your consent where required by applicable law), or if you have opted in to
      receive direct marketing emails from us, we may use your personal
      information to send you marketing information about The Expert Service,
      including, but not limited to, new Products, new Designers, new features
      of The Expert Service, and/or other products and services of The Expert,
      Designers and/or merchants/vendors of the Products that we think may
      interest you.
    </p>
    <p>
      You may manage the receipt of marketing and non-transactional email
      communications you receive from us by clicking on the
      &ldquo;unsubscribe&rdquo; link located on the bottom of any of our
      marketing e-mails.
    </p>
    <p>
      We will use commercially reasonable efforts to process your requests to
      opt-out of email marketing communications in a timely manner.&nbsp;Note
      that you cannot opt out of receiving transactional e-mails or
      communications related to the use of The Expert Service or your purchase
      of Products (e.g., requests for support), which, for clarification, are
      not marketing communications.
    </p>
    <p>
      In addition, if you have opted-in to our Text Service, we may send you
      SMS/MMS/OTT text messages as set forth in our{' '}
      <LinkWithUnderline href="https://www.theexpert.com/terms">
        Text Service T&Cs
      </LinkWithUnderline>
      . You can opt-out at of the Text Service at any time by following the
      procedures set forth in the{' '}
      <LinkWithUnderline href="https://www.theexpert.com/terms">
        Text Service T&Cs
      </LinkWithUnderline>
      .
    </p>
    <br />
    <H3 id="accessing-updating-and-correcting-personal-information">
      9. Accessing, Updating and Correcting Personal information
    </H3>
    <p>
      If you would like to correct or update certain personal information (such
      as your contact information), please{' '}
      <LinkWithUnderline href="#how-to-contact-us">
        contact us
      </LinkWithUnderline>{' '}
      and we will use reasonable efforts to correct and/or update such
      information.
    </p>
    <br />
    <H3 id="third-party-social-plug-ins">10. Third Party Social Plug-Ins</H3>
    <p>
      On or through the Site, we provide third-party &ldquo;share&rdquo; buttons
      which enable you to share certain content via social media sites (e.g.,
      Facebook, Twitter, Instagram, YouTube, and LinkedIn).&nbsp;These
      &ldquo;share&rdquo; buttons may function as web beacons when you interact
      with the button.&nbsp;Please note that when you &ldquo;share&rdquo; using
      the buttons, you may send to the third party provider of the
      &ldquo;share&rdquo; button the information that you are viewing.&nbsp;If
      you are not logged into your account with the third party provider, then
      the third party may not know your identity.&nbsp;If you are logged in to
      your account with the third party, then the third party may be able to
      link information or actions about your interactions with the Site to your
      account with the applicable third party provider. Please refer to each
      third party&rsquo;s privacy policies to learn more about its data
      practices.
    </p>
    <br />
    <H3 id="external-websites">11. External Websites</H3>
    <p>
      On or through the Site, we may provide or make available, for
      informational purposes only, links to other websites or resources and over
      which we do not have control (&ldquo;<Strong>External Websites</Strong>
      &rdquo;).&nbsp;Such links do not constitute an endorsement by The Expert
      of those External Websites, and are provided to you only as a convenience.
      By clicking on links to External Websites, the operators of the External
      Websites may collect your personal information.&nbsp;We are not
      responsible for the content or data collection practices of those External
      Websites, and your use of External Websites is subject to their respective
      terms of use and privacy policies.
    </p>
    <br />
    <H3 id="data-retention">12. Data Retention</H3>
    <p>
      Personal information is processed for the period necessary to fulfill the
      purposes for which it is collected (for example, in connection with The
      Expert Service provided to you pursuant to the applicable terms and/or
      agreement entered into with The Expert with respect to your use of The
      Expert Service or purchase of any Products, Consultations and/or
      Services), to comply with legal and regulatory obligations and for the
      duration of any period necessary to establish, exercise or defend any
      legal rights.
    </p>
    <br />
    <H3 id="data-security-and-protection">13. Data Security and Protection</H3>
    <p>
      We have put in place reasonable and appropriate security measures designed
      to prevent your personal information from being accidentally lost, used or
      accessed in an unauthorized way, altered or disclosed. For example, we use
      encryption, secure socket layer, firewalls, and password protection. In
      addition, we limit access to personal information to those employees,
      agents, contractors and the third parties who have a business
      need-to-know.
    </p>
    <p>
      We also have procedures in place to deal with any suspected data security
      breach.&nbsp;However, no method of transmission over the Internet, or
      method of electronic storage, is 100% secure, and while we take reasonable
      steps to provide secure services, by using The Expert Service, you
      understand and assume the risks associated with your activities on the
      internet.
    </p>
    <p>
      In addition, you have responsibility and control over protecting your
      personal information. For example, you, and not The Expert is responsible
      for safeguarding your password, and other authentication information you
      use to access The Expert Service.&nbsp;You should not disclose your
      password and/or authentication information to any third party and should
      immediately{' '}
      <LinkWithUnderline href="#how-to-contact-us">
        contact us
      </LinkWithUnderline>{' '}
      of any unauthorized use of your password or you have reason to believe
      that your information and/or account is no longer secure.
    </p>
    <p>
      In addition, as described in this Privacy Notice, The Expert cannot
      secure, and has no responsibility for, any personal information that you
      share or release on your own or that you request us to share or release or
      share with other users of The Expert Service.
    </p>
    <br />
    <H3 id="european-privacy-rights">14. European Privacy Rights</H3>
    <p>
      The European Union&rsquo;s General Data Protection Regulation and certain
      other countries&rsquo; data protection laws provide certain rights for
      data subjects. If you are a resident of the European Economic Area, the
      United Kingdom or another country with data protection laws that provide
      for certain data subject rights, you may submit a request to exercise your
      rights.
    </p>
    <br />
    <p>
      Users in the European Economic Area and the United Kingdom have the
      following rights:
    </p>
    <br />
    <p>
      <Strong>The right to be informed</Strong> - that&rsquo;s an obligation on
      us to inform you how we use your personal data (and that&rsquo;s what
      we&rsquo;re doing in this Privacy Notice)
    </p>
    <br />
    <p>
      <Strong>Right of access</Strong> - The Controller offers you access to
      your personal data we process. This means you can contact us and request
      from us a confirmation whether or not your personal data are being
      processed and if so, you have the right to request access to your data,
      which we will provide to you in the form of a so-called
      &quot;registry&quot; (stating, in particular, purposes, categories of
      personal data, categories of recipients of personal data, storage periods
      or criteria for determining storage periods).
    </p>
    <br />
    <p>
      <Strong>Right to rectification</Strong> - You have the right to have
      inaccurate personal data we have stored about you rectified.
    </p>
    <br />
    <p>
      <Strong>Right to erasure</Strong> - You may also ask us to erase your
      personal data from our systems. We will comply with such requests unless
      we have a legitimate ground to not delete your personal data.
    </p>
    <br />
    <p>
      <Strong>Right to restriction of processing</Strong> - You may request us
      to restrict certain processing of your personal data. If you restrict
      certain processing of your personal data, this may lead to fewer
      possibilities to use our Services and website.
    </p>
    <br />
    <p>
      <Strong>Right to data portability</Strong> - You have the right to receive
      your personal data from us in a structured, commonly used and
      machine-readable format in order to transmit the personal data to another
      controller.
    </p>
    <br />
    <p>
      <Strong>Right to object</Strong> - that&rsquo;s a right for you to object
      to us processing your personal data (for example, if you object to us
      processing your data for direct marketing).
    </p>
    <br />
    <p>
      <Strong>
        Rights in relation to automated decision-making and profiling
      </Strong>{' '}
      - that&rsquo;s a right you have for us to be transparent about any
      profiling we do, or any automated decision-making.
    </p>
    <br />
    <p>
      <Strong>Right to withdraw Consent</Strong> — that&rsquo;s the right to
      revoke any consent you may have previously given us at any time, if we
      have collected and processed your personal information with your consent.
      Withdrawing your consent will not affect the lawfulness of any processing
      we conducted with your consent prior to your withdrawal, nor will it
      affect processing of your personal information conducted in reliance on
      lawful processing grounds other than consent.
    </p>
    <br />
    <p>
      <Strong>How to use your rights</Strong> - You may exercise your rights
      above in writing by sending an email to the contact details in How to
      Contact Us. You will not have to pay a fee to exercise any of your rights
      unless your request is clearly unfounded, repetitive or excessive.
      Alternatively, we may refuse to comply with your request in these
      circumstances. We may require confirmation of your identity depending on
      your request. We respond to all requests we receive from individuals
      wishing to exercise their data rights in accordance with applicable data
      protection laws.
    </p>
    <br />
    <p>
      In case you consider our processing activities of your personal data to be
      inconsistent with the applicable data protection laws, you may lodge a
      complaint with the local supervisory authority for data protection.
    </p>
    <br />
    <H3 id="california-privacy">
      15. California Privacy Rights - Shine the Light
    </H3>
    <p>
      California residents who have provided us with personal information have
      the right (under California Civil Code&sect;&nbsp;1798.83) to request and
      obtain from us, once each year, the details of any personal information we
      shared with a third party for that third party&rsquo;s direct marketing
      purposes during the prior calendar year. The details would include the
      categories of personal information and the names and addresses of the
      third party with which it was shared.
    </p>
    <p>
      To request information about this sharing, you may submit a request with
      &quot;Your California Privacy Rights&quot; in the subject line, along with
      your first and last name, and complete mailing address (including street
      address, city, state, and zip code) by either: sending an email message or
      writing to us at the email and/or mailing address specified in the{' '}
      <LinkWithUnderline href="#how-to-contact-us">
        How to Contact Us
      </LinkWithUnderline>{' '}
      Section.
    </p>
    <p>
      If you do not submit and send your request with complete information as
      set forth above, we will not be able to process it.
    </p>
    <br />
    <H3 id="nevada-privacy">16. Nevada Privacy Rights</H3>
    <p>
      Under Nevada law, certain Nevada residents may opt out of the sale of
      covered personal information (which includes first and last name, address,
      email address, phone number, Social Security Number, or an identifier that
      allows a specific person to be contacted either physically or online) to a
      person for that person to license or sell such information to additional
      persons.
    </p>
    <p>
      The Expert does not currently sell your covered information as defined
      under applicable Nevada law; however, you may submit an opt-out request,
      which we will honor as required by Nevada law should we engage in such a
      sale in the future.
    </p>
    <p>
      You may submit an opt-out request by sending an email message or writing
      to us at the email and/or mailing address specified in the{' '}
      <LinkWithUnderline href="#how-to-contact-us">
        How to Contact Us
      </LinkWithUnderline>{' '}
      Section; along with your full name, any user name, and complete mailing
      address (including street address, city, state, and zip code), email
      address (so that we can contact you, if needed, in connection with the
      request) and confirmation that you are a Nevada resident.
    </p>
    <p>
      You must provide us with information reasonably requested by us to verify
      your identity and the authenticity of the request.&nbsp;We typically
      process these requests within 60 days from the day we receive it (and will
      notify you within that period if we need an additional 30 days to do so).
    </p>
    <br />
    <H3 id="international-transfers">17. International Transfers</H3>
    <p>
      The Expert is based in the United States. The personal information that we
      collect are sent to and stored on servers located in the United
      States.&nbsp;Such storage is necessary in order to process the
      information. The Expert may transfer the personal data that we collect
      from you to our other offices and/or to the third parties mentioned in the
      circumstances described above, which may be situated outside of your
      country or regional area, and may be processed by staff operating outside
      of your country or regional area. In particular, information provided to
      us or collected by us likely will be transferred to and processed in the
      United States by us or our Affiliates and our respective agents and
      contractors.
    </p>
    <br />
    <p>
      In such a case personal data of a User in the European Union is
      transferred, we transfer your personal data with appropriate safeguards in
      place to protect your personal data, such as standard contract clauses or
      binding internal company rules. Regardless of the country in which your
      personal data is processed, the Controller takes reasonable technical,
      legal and organizational measures to ensure that the level of protection
      is the same as in the European Union, United Kingdom, and the European
      Economic Area.
    </p>
    <br />
    <H3 id="how-to-contact-us">18. How to Contact Us</H3>
    <p>
      If you have any questions about this Privacy Notice or our{' '}
      <LinkWithUnderline href="/blog/cookie-policy">
        Cookie Policy
      </LinkWithUnderline>
      , including any requests to exercise your legal rights, please contact us
      as follows:
    </p>
    <p>
      By email at:
      <LinkWithUnderline href={`mailto:${EMAILS.CUSTOMER_SUPPORT}`}>
        {EMAILS.CUSTOMER_SUPPORT}
      </LinkWithUnderline>
      ; <em>Subject Line</em>: Privacy Request
    </p>
    <PureSSRIndicator />
  </Box>
)

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

PrivacyPolicy.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true })

PrivacyPolicy.seoProps = {
  description:
    'Data collection, use, protection, and privacy practices of The Expert, Inc.',
}

export default PrivacyPolicy
